/* eslint @typescript-eslint/strict-boolean-expressions: off */

import Link from "next/link";
import React from "react";
import { AuthError } from "lib/api/auth";
import { ButtonOld } from "components/ButtonOld/ButtonOld";
import { Button } from "components/Button/Button";
import { CSRFContext } from "gui/csrf/csrf";
import { FieldButtons, LeftButtons } from "gui/input/fields";
import { FieldError } from "gui/input/fields";
import { FieldFooter } from "gui/input/fields";
import { FieldHelp } from "gui/input/fields";
import { FieldLabel } from "gui/input/fields";
import { FieldLegal } from "gui/input/fields";
import { Fields } from "gui/input/fields";
import { GetStaticPaths } from "next";
import { GetStaticProps } from "next";
import { Header } from "shared/Header/Header";
import { InputText } from "gui/input/input";
import { LoadingPage } from "gui/loading/page";
import { NotFoundPage } from "pages/404";
import { PageHead } from "gui/head/head";
import { PageMain } from "gui/page/page";
import { MultiPoster, Poster } from "gui/poster/poster";
import { RedirectPage } from "gui/redirect/page";
import { SessionContext } from "gui/session/session";
import { authErrorLabels } from "gui/auth/errors";
import { firstQueryValue } from "lib/links/query";
import { frontend } from "lib/env/env";
import { HTTPError, httpPost } from "lib/http/http";
import { parseBrowserQuery } from "lib/links/query";
import { useRouter } from "next/router";
import { useLoginUrl } from "lib/hooks/useLoginUrl";
import { useSignupUrl } from "lib/hooks/useSignupUrl";
import { sanitizeRedirectLink } from "lib/links/links";
import { Icon } from "gui/icon/icon";
import isEmail from "validator/lib/isEmail";
import styles from "pages/auth/[slug].module.css";
import "@rainbow-me/rainbowkit/styles.css";
import { useLoginInWithEth } from "shared/web3/useLoginWithEth";

interface Props {
  slug: string;
  preview: boolean;
}

export const AuthPage: React.FC<Props> = (props) => {
  const { session, sessionLoading } = React.useContext(SessionContext);
  const next = firstQueryValue(parseBrowserQuery(), "next") || "/";
  const sanitizedNext = sanitizeRedirectLink(next);

  if (sessionLoading) {
    return <LoadingPage />;
  }

  if (session) {
    return <RedirectPage href={sanitizedNext || "/"} replace />;
  }

  const FormComponent =
    props.slug === "login"
      ? FormLogin
      : props.slug === "register"
      ? FormRegister
      : props.slug === "registerConfirm"
      ? EmailVerification
      : props.slug === "reset"
      ? FormReset
      : props.slug === "resetConfirm"
      ? FormResetConfirm
      : null;

  const pageTitle =
    props.slug === "login"
      ? "Sign in to Dune"
      : props.slug === "register"
      ? "Sign up for Dune"
      : props.slug === "registerConfirm"
      ? "Confirm email"
      : props.slug === "reset"
      ? "Reset password"
      : props.slug === "resetConfirm"
      ? "Confirm password reset"
      : undefined;

  if (!FormComponent) {
    return <NotFoundPage />;
  }

  return (
    <>
      <PageHead preview={props.preview} title={pageTitle} />
      <Header />
      <PageMain size="md">
        <FormComponent {...props} />
      </PageMain>
    </>
  );
};

export const FormLogin: React.FC<Props> = () => {
  const [loading, setLoading] = React.useState(false);

  const query = parseBrowserQuery();
  const ref = firstQueryValue(query, "ref");
  const errorParam = firstQueryValue(query, "error");
  const errorKey = errorParam in authErrorLabels ? errorParam : undefined;
  const error = errorKey ? new AuthError(errorKey) : undefined;
  const { csrf } = React.useContext(CSRFContext);

  const header =
    ref === "registerConfirm"
      ? "Your email was confirmed. Please sign in."
      : ref === "resetConfirm"
      ? "Your password was changed. Please sign in."
      : undefined;

  // The "next" form field controls where the user is redirected after login.
  // By default we redirect to the Next frontend's personalized home page.
  const next = firstQueryValue(query, "next") || frontend();

  // The login form works differently than the rest of the auth forms
  // since a successful login results in a browser redirect.
  return (
    <Poster>
      <form
        method="post"
        action="/api/auth"
        aria-label="Sign in"
        onSubmit={() => setLoading(true)}
      >
        <Fields>
          {error && <FieldError error={error} />}
          <input type="hidden" name="csrf" value={csrf || ""} />
          <input type="hidden" name="action" value="login" />
          <input type="hidden" name="next" value={next} />
          {header && <FieldHelp>{header}</FieldHelp>}
          <FieldUsername label="Username or email" />
          <FieldPasswordWithReset label="Password" />
          <hr />
          <FieldButtons>
            <ButtonSubmit loading={loading}>Sign in</ButtonSubmit>
            <ButtonRegister>
              <span className={styles.mobile}>Sign up for free</span>
              <span className={styles.desktop}>
                Need an account? Sign up here
              </span>
            </ButtonRegister>
          </FieldButtons>
        </Fields>
      </form>
      <FieldFooter className={styles.footer}>
        <ButtonEthereumLogin />
      </FieldFooter>
    </Poster>
  );
};

const FormRegister: React.FC<Props> = () => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<AuthError>();
  const { csrf } = React.useContext(CSRFContext);
  const { push } = useRouter();
  const emailInputRef = React.useRef<HTMLInputElement>(null);

  // next is the location that the user should redirect to after registering.
  const next = firstQueryValue(parseBrowserQuery(), "next");

  const onSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const { username, password, email } = findFormValues(event);

    if (!isEmail(email)) {
      if (emailInputRef.current) {
        emailInputRef.current.setCustomValidity(
          `Must be a valid email like "mail@example.com". Must contain "@" and "."`
        );
        emailInputRef.current.reportValidity();
      }
      return;
    }

    const nextPage = new URL("/auth/registerConfirm", frontend());
    nextPage.searchParams.set("username", username);
    if (next !== undefined) {
      // Propogate the post-registration redirect path to the confirmation page.
      nextPage.searchParams.set("next", next);
    }
    setLoading(true);
    register(csrf!, username, password, email)
      .then(() => push(nextPage.pathname + nextPage.search))
      .catch((err) => setError(new AuthError(err.message)))
      .then(() => setLoading(false));
  };

  return (
    <Poster>
      <form onSubmit={onSubmit} aria-label="Sign up">
        <Fields>
          {error && <FieldError error={error} />}
          <FieldUsername label="Pick a username" />
          <FieldEmail ref={emailInputRef} label="Your email address" />
          <FieldPassword label="Choose a password" />

          <hr />
          <FieldButtons>
            <ButtonSubmit loading={loading}>Create account</ButtonSubmit>
            <ButtonLogin>Cancel</ButtonLogin>
          </FieldButtons>
          <FieldLegal>
            By creating an account you agree to the{" "}
            <a href="/terms" target="_blank">
              Terms
            </a>{" "}
            and confirm that you have read the{" "}
            <a href="/privacy" target="_blank">
              Privacy Policy
            </a>
            .
          </FieldLegal>
        </Fields>
      </form>
    </Poster>
  );
};

const EmailVerification: React.FC<Props> = () => {
  const [submitting, setSubmitting] = React.useState(false);
  const [resending, setResending] = React.useState(false);
  const [error, setError] = React.useState<AuthError>();
  const [username, setUsername] = React.useState<string>(
    firstQueryValue(parseBrowserQuery(), "username")
  );
  // next is the location that the user should redirect to after registering.
  const next = firstQueryValue(parseBrowserQuery(), "next");

  const [helperText, setHelperText] = React.useState<string | undefined>(
    "We've sent you an email with a confirmation code. Please use the code to confirm your email address below."
  );
  const { csrf } = React.useContext(CSRFContext);
  const { push } = useRouter();

  const onResend = () => {
    setResending(true);
    resendConfirmationCode(csrf!, username)
      .then(() => {
        setResending(false);
        setHelperText(
          "Your verification email is on its way. It may take a little while to arrive."
        );
        setError(undefined);
      })
      .catch((err) => {
        setError(new AuthError(err.message));
        setHelperText(undefined);
        setResending(false);
      });
  };

  const nextPage = new URL("/auth/login", frontend());
  nextPage.searchParams.set("ref", "registerConfirm");
  if (next !== undefined) {
    // Propogate the post-registration redirect path to the login page.
    nextPage.searchParams.set("next", next);
  }
  const onSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const { code } = findFormValues(event);
    if (code.trim()) {
      setSubmitting(true);
      registerConfirm(csrf!, username, code.trim())
        .then(() => push(nextPage.pathname + nextPage.search))
        .catch((err) => setError(new AuthError(err.message)))
        .then(() => setSubmitting(false));
    }
  };

  return (
    <MultiPoster>
      {helperText && <p className={styles["helper-text"]}>{helperText}</p>}
      <form onSubmit={onSubmit} aria-label="Sign up confirmation">
        <Fields>
          <FieldUsername
            label="Username or email"
            defaultValue={username}
            onValueChange={setUsername}
          />
          <FieldCode label="Confirmation code" />
          <hr />
          <LeftButtons>
            <ButtonSubmit
              loading={submitting}
              disabled={submitting || resending}
            >
              Submit code
            </ButtonSubmit>
            <ButtonResend
              onClick={onResend}
              loading={resending}
              disabled={submitting}
            >
              {resending ? "Resending..." : "Resend"}
            </ButtonResend>
          </LeftButtons>
          {error && (
            <FieldFooter>
              <FieldError error={error} />
            </FieldFooter>
          )}
        </Fields>
      </form>
    </MultiPoster>
  );
};

const FormReset: React.FC<Props> = () => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<AuthError>();
  const { csrf } = React.useContext(CSRFContext);
  const { push } = useRouter();

  const onSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const { username } = findFormValues(event);
    if (username.includes("@")) {
      setError(new AuthError("InvalidUsernameException"));
    } else if (username) {
      const encoded = encodeURIComponent(username);
      const resetConfirmPath = `/auth/resetConfirm?username=${encoded}`;
      setLoading(true);
      reset(csrf!, username)
        .then(() => push(resetConfirmPath))
        .catch((err) => setError(new AuthError(err.message)))
        .then(() => setLoading(false));
    }
  };

  return (
    <Poster>
      <form onSubmit={onSubmit} aria-label="Reset password">
        <Fields>
          {error && <FieldError error={error} />}
          <FieldUsername label="What's your username?" />
          <hr />
          <FieldButtons>
            <ButtonSubmit loading={loading}>Send reset email</ButtonSubmit>
            <ButtonLogin>Cancel</ButtonLogin>
          </FieldButtons>
        </Fields>
      </form>
    </Poster>
  );
};

const FormResetConfirm: React.FC<Props> = () => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<AuthError>();
  const username = firstQueryValue(parseBrowserQuery(), "username");
  const { csrf } = React.useContext(CSRFContext);
  const { push } = useRouter();

  const onSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const { password, code } = findFormValues(event);
    if (password && code.trim()) {
      setLoading(true);
      resetConfirm(csrf!, username, password, code.trim())
        .then(() => push(`/auth/login?ref=resetConfirm`))
        .catch((err) => setError(new AuthError(err.message)))
        .then(() => setLoading(false));
    }
  };

  if (!username) {
    throw new Error("Missing password reset confirmation username");
  }

  return (
    <Poster>
      <form onSubmit={onSubmit} aria-label="Reset password confirmation">
        <Fields>
          {error && <FieldError error={error} />}
          <FieldHelp>Check your email for a confirmation code.</FieldHelp>
          <FieldPassword label="Choose a new password" />
          <FieldCode label="Confirmation code" />
          <hr />
          <FieldButtons>
            <ButtonSubmit loading={loading}>Save new password</ButtonSubmit>
            <ButtonLogin>Cancel</ButtonLogin>
          </FieldButtons>
        </Fields>
      </form>
    </Poster>
  );
};

const FieldUsername: React.FC<{
  label: React.ReactNode;
  defaultValue?: string;
  onValueChange?: (val: string) => void;
}> = (props) => {
  return (
    <FieldLabel label={props.label}>
      <InputText
        size="lg"
        name="username"
        autoComplete="username"
        onChange={(event: React.FormEvent<HTMLInputElement>) => {
          if (props.onValueChange) {
            props.onValueChange(event.currentTarget.value);
          }
        }}
        placeholder="satoshi"
        defaultValue={props.defaultValue}
        required
      />
    </FieldLabel>
  );
};

const FieldEmail = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { label: React.ReactNode }
>((props, ref) => {
  // Load the email from the url if there. This is useful for when a user
  // is invited by email
  const emailFromUrl = firstQueryValue(parseBrowserQuery(), "email");
  const [email, setEmail] = React.useState<string>(emailFromUrl || "");
  return (
    <FieldLabel label={props.label}>
      <InputText
        size="lg"
        name="email"
        placeholder="mail@example.com"
        type="email"
        autoComplete="email"
        value={email}
        required
        ref={ref}
        onChange={(e) => {
          setEmail(e.target.value);
          if (isEmail(e.target.value)) {
            e.target.setCustomValidity("");
            e.target.reportValidity();
          }
        }}
      />
    </FieldLabel>
  );
});

const FieldPassword: React.FC<{
  label: React.ReactNode;
}> = (props) => {
  return (
    <FieldLabel label={props.label}>
      <InputText
        size="lg"
        name="password"
        type="password"
        autoComplete="password"
        minLength={8}
        required
      />
    </FieldLabel>
  );
};

const FieldPasswordWithReset: React.FC<{
  label: React.ReactNode;
  withPasswordReset?: boolean;
}> = (props) => {
  const label = (
    <div className={styles["password-field-label"]}>
      <span>{props.label}</span>
      <LinkReset tabIndex={-1}>Reset password</LinkReset>
    </div>
  );

  return (
    <FieldLabel label={label}>
      <InputText
        size="lg"
        name="password"
        type="password"
        minLength={8}
        required
      />
    </FieldLabel>
  );
};

const FieldCode: React.FC<{
  label: React.ReactNode;
}> = (props) => {
  return (
    <FieldLabel label={props.label}>
      <InputText
        size="lg"
        name="code"
        placeholder="123456"
        inputMode="numeric"
        autoComplete="off"
        required
      />
    </FieldLabel>
  );
};

const ButtonResend: React.FC<{
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
}> = (props) => {
  return (
    <ButtonOld
      {...props}
      light
      color2={!props.disabled}
      gray={Boolean(props.disabled)}
    >
      {props.children}
    </ButtonOld>
  );
};

const ETHEREUM_ERROR_MAPPING: Record<string, Error> = {
  userNotFoundForAddress: new Error(
    "No user linked with this address. Link your address in Settings."
  ),
};

const ButtonEthereumLogin: React.FC<{}> = (props) => {
  const { login: loginWithEth, error, loading } = useLoginInWithEth();
  const { children, ...buttonProps } = props;
  const parseError = () => {
    if (error === null) return null;
    if (
      error instanceof HTTPError &&
      ETHEREUM_ERROR_MAPPING[error.errorKey()]
    ) {
      return ETHEREUM_ERROR_MAPPING[error.errorKey()];
    }

    return new Error("Sign in with Ethereum failed. Please Try again.");
  };
  const parsedError = parseError();

  return (
    <>
      {parsedError && <FieldError error={parsedError} />}
      <Button
        {...buttonProps}
        disabled={loading}
        onClick={loginWithEth}
        className={styles.ethSignIn}
        theme="primary"
        size="L"
      >
        <Icon icon="ethereum" />{" "}
        {loading ? "Verifying" : "Sign in with Ethereum"}
      </Button>
    </>
  );
};

const ButtonSubmit: React.FC<{
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
}> = (props) => {
  return (
    <ButtonOld
      {...props}
      type="submit"
      color2={!props.disabled}
      gray={Boolean(props.disabled)}
    >
      {props.children}
    </ButtonOld>
  );
};

const ButtonRegister: React.FC = (props) => {
  const signupUrl = useSignupUrl();

  return (
    <Link href={signupUrl}>
      <a>
        <ButtonOld color2 light>
          {props.children}
        </ButtonOld>
      </a>
    </Link>
  );
};

const ButtonLogin: React.FC = (props) => {
  const loginUrl = useLoginUrl();

  return (
    <Link href={loginUrl}>
      <a>
        <ButtonOld color2 light>
          {props.children}
        </ButtonOld>
      </a>
    </Link>
  );
};

const LinkReset: React.FC<{ tabIndex?: number }> = (props) => {
  return (
    <Link href="/auth/reset">
      <a tabIndex={props.tabIndex}>{props.children}</a>
    </Link>
  );
};

const findFormValues = (event: React.SyntheticEvent) => {
  // Grab the form input values directly from the form submit event.
  // We cannot use controlled input fields for the username or password
  // since controlled fields won't capture autofilled input values.
  const target = event.target as HTMLFormElement;

  return {
    username: (target.username as HTMLInputElement | undefined)?.value ?? "",
    password: (target.password as HTMLInputElement | undefined)?.value ?? "",
    email: (target.email as HTMLInputElement | undefined)?.value ?? "",
    code: (target.code as HTMLInputElement | undefined)?.value ?? "",
  };
};

export const register = async (
  csrf: string,
  username: string,
  password: string,
  email: string
) => {
  return httpPost<void>("/api/auth", {
    action: "register",
    csrf,
    username,
    password,
    email,
  });
};

export const registerConfirm = async (
  csrf: string,
  usernameOrEmail: string,
  code: string
) => {
  return httpPost<void>("/api/auth", {
    action: "registerConfirm",
    csrf,
    usernameOrEmail,
    code,
  });
};

export const resendConfirmationCode = async (
  csrf: string,
  usernameOrEmail: string
) => {
  return httpPost<void>("/api/auth", {
    action: "resendEmailConfirmationCode",
    csrf,
    usernameOrEmail,
  });
};

export const reset = async (csrf: string, username: string) => {
  return httpPost<void>("/api/auth", {
    action: "reset",
    csrf,
    username,
  });
};

export const resetConfirm = async (
  csrf: string,
  username: string,
  password?: string,
  code?: string
) => {
  return httpPost<void>("/api/auth", {
    action: "resetConfirm",
    csrf,
    username,
    password,
    code,
  });
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = [
    { params: { slug: "login" } },
    { params: { slug: "register" } },
    { params: { slug: "registerConfirm" } },
    { params: { slug: "reset" } },
    { params: { slug: "resetConfirm" } },
  ];

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props> = async (ctx) => {
  const slug = ctx.params!.slug as string;
  const preview = Boolean(ctx.preview);

  return {
    props: {
      slug,
      preview,
    },
  };
};

export default AuthPage;
